import { useNavigate } from "react-router-dom";
import { useKeepAliveContext } from "./use-keep-alive-context";

/**
 * Smart navigation hook that handles tab management
 * 智能导航 Hook，处理 Tab 管理
 */
export const useSmartNavigate = () => {
  const navigate = useNavigate();
  const { tabs, closeTab } = useKeepAliveContext();

  /**
   * Navigate to workorder detail page and close other workorder detail tabs
   * 导航到 workorder 详情页，并关闭其他 workorder 详情 tabs
   * @param {string|number} id - Workorder ID
   */
  const navigateToWorkorderDetail = (id) => {
    const newPath = `/facility/workorder-detail/${id}`;
    
    // Close other workorder-detail tabs (except the one we're navigating to)
    // 关闭其他 workorder-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/facility/workorder-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Navigate to company detail page and close other company detail tabs
   * 导航到 company 详情页，并关闭其他 company 详情 tabs
   * @param {string|number} id - Company ID
   */
  const navigateToCompanyDetail = (id) => {
    const newPath = `/admin/company-detail/${id}`;
    
    // Close other company-detail tabs (except the one we're navigating to)
    // 关闭其他 company-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/admin/company-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Navigate to facility detail page and close other facility detail tabs
   * 导航到 facility 详情页，并关闭其他 facility 详情 tabs
   * @param {string|number} id - Facility ID
   */
  const navigateToFacilityDetail = (id) => {
    const newPath = `/admin/facility-detail/${id}`;
    
    // Close other facility-detail tabs (except the one we're navigating to)
    // 关闭其他 facility-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/admin/facility-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Navigate to equipment detail page and close other equipment detail tabs
   * 导航到 equipment 详情页，并关闭其他 equipment 详情 tabs
   * @param {string|number} id - Equipment ID
   */
  const navigateToEquipmentDetail = (id) => {
    const newPath = `/facility/equipment-detail/${id}`;
    
    // Close other equipment-detail tabs (except the one we're navigating to)
    // 关闭其他 equipment-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/facility/equipment-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Navigate to project detail page and close other project detail tabs
   * 导航到 project 详情页，并关闭其他 project 详情 tabs
   * @param {string|number} id - Project ID
   */
  const navigateToProjectDetail = (id) => {
    const newPath = `/facility/project-detail/${id}`;
    
    // Close other project-detail tabs (except the one we're navigating to)
    // 关闭其他 project-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/facility/project-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Navigate to functional testing detail page and close other functional testing detail tabs
   * 导航到 functional testing 详情页，并关闭其他 functional testing 详情 tabs
   * @param {string|number} id - Functional Testing ID
   */
  const navigateToFunctionalTestingDetail = (id) => {
    const newPath = `/facility/functional-testing-detail/${id}`;
    
    // Close other functional-testing-detail tabs (except the one we're navigating to)
    // 关闭其他 functional-testing-detail tabs（除了即将导航到的这个）
    tabs.forEach((tab) => {
      if (
        tab.routePath.startsWith("/facility/functional-testing-detail/") &&
        tab.routePath !== newPath
      ) {
        closeTab(tab.routePath);
      }
    });

    // Navigate to the new path
    navigate(newPath);
  };

  /**
   * Generic method to navigate and close tabs by pattern
   * 通用方法：根据路径模式导航并关闭 tabs
   * @param {string} newPath - The new path to navigate to
   * @param {string} pattern - The pattern to match tabs to close
   */
  const navigateAndCloseTabs = (newPath, pattern) => {
    // Close tabs matching the pattern (except the new path)
    tabs.forEach((tab) => {
      if (tab.routePath.startsWith(pattern) && tab.routePath !== newPath) {
        closeTab(tab.routePath);
      }
    });

    navigate(newPath);
  };

  return {
    navigate, // Original navigate function
    navigateToWorkorderDetail,
    navigateToCompanyDetail,
    navigateToFacilityDetail,
    navigateToEquipmentDetail,
    navigateToProjectDetail,
    navigateToFunctionalTestingDetail,
    navigateAndCloseTabs,
  };
};

